/**
 * Themed-shape variant — per-category surface metadata.
 *
 * Six hardcoded categories. Each carries:
 *   - slug + display title + plain tagline
 *   - word list (used by the engine for placement)
 *   - chime kind (audio, not imagery — composed in `sound.ts` from
 *     `juice/audio.ts` primitives)
 *   - atmosphere image keys (light + dark) — generated PNGs served from
 *     /generated/wordsearch-themedshape/<slug>-<mode>.png. NO hand-drawn
 *     SVG depictive imagery anywhere — the asset pipeline is the source of
 *     truth for any visual that wants to depict something.
 *
 * The palette itself is in `palette.ts`, keyed by the same slug.
 *
 * Word banks live in `@rendever/puzzle-content/word-search` and are produced
 * by the `tools/content-pipeline` LLM-gen + LLM-as-judge pipeline against
 * `tools/content-pipeline/manifests/wordsearch-themedshape-v1.yaml`. To
 * refresh: `pnpm content-pipeline --pack wordsearch-themedshape-v1`. Each
 * file holds ~50–60 senior-audience-vetted words, 4–9 letters, A–Z only.
 * Each round, the engine draws 6–8 from the pool — far more variety than
 * the hand-curated 8 we used to ship.
 */

import { WORD_BANKS, type WordBankSlug } from '@rendever/puzzle-content/word-search';
import { dailySeed, makeSeededRandom } from '@rendever/puzzle-core/seed';

/**
 * The themes this variant surfaces. One per shipped word bank — the slug is the
 * join key across the bank, the palette, and the generated atmosphere art.
 */
export type CategorySlug = WordBankSlug;

export type ChimeKind =
  | 'bird-trill' // garden
  | 'piano-note' // music
  | 'soft-bell' // kitchen
  | 'projector-click' // films
  | 'ship-bell' // travel
  | 'wood-tap'; // animals

export interface ThemeSurface {
  slug: CategorySlug;
  title: string;
  tagline: string;
  /** Full word pool for the category — uppercase, 4–9 letters. The engine
   *  per-round samples `WORDS_PER_ROUND` of these via
   *  {@link sampleWordsForRound}; do not pass the whole pool to the grid. */
  words: readonly string[];
  /** Themed audio chime, played on each found-word event. */
  chime: ChimeKind;
  /** Atmosphere image basename — served as `/generated/.../{slug}-{mode}.png` */
  atmosphereSlug: string;
}

export const THEME_SURFACES: readonly ThemeSurface[] = [
  {
    slug: 'garden',
    title: 'Garden',
    tagline: 'Flowers and growing things.',
    words: WORD_BANKS.garden,
    chime: 'bird-trill',
    atmosphereSlug: 'garden',
  },
  {
    slug: 'music',
    title: 'Music',
    tagline: 'Instruments, notes, and songs.',
    words: WORD_BANKS.music,
    chime: 'piano-note',
    atmosphereSlug: 'music',
  },
  {
    slug: 'kitchen',
    title: 'Kitchen',
    tagline: 'Cooking, baking, and the pantry.',
    words: WORD_BANKS.kitchen,
    chime: 'soft-bell',
    atmosphereSlug: 'kitchen',
  },
  {
    slug: 'films',
    title: 'Films',
    tagline: 'The pictures and the picture-house.',
    words: WORD_BANKS.films,
    chime: 'projector-click',
    atmosphereSlug: 'films',
  },
  {
    slug: 'travel',
    title: 'Travel',
    tagline: 'Trains, planes, and far places.',
    words: WORD_BANKS.travel,
    chime: 'ship-bell',
    atmosphereSlug: 'travel',
  },
  {
    slug: 'animals',
    title: 'Animals',
    tagline: 'Field, forest, and farm.',
    words: WORD_BANKS.animals,
    chime: 'wood-tap',
    atmosphereSlug: 'animals',
  },
] as const;

export function getThemeSurface(slug: CategorySlug): ThemeSurface {
  const found = THEME_SURFACES.find((t) => t.slug === slug);
  if (!found) throw new Error(`Unknown theme surface: ${slug}`);
  return found;
}

/** How many words from the pool we place per round. The grid is 10–12 cells
 *  square, so 8 is the comfortable ceiling — more and placement starts to
 *  fail. The pool sizes (50+) are intentionally far larger than this so that
 *  back-to-back rounds in the same category never see the same set twice
 *  in a row. */
export const WORDS_PER_ROUND = 8;

export function dailyWordSearchSeed(slug: CategorySlug, now: Date = new Date()): number {
  return dailySeed(`word-search:${slug}`, now);
}

/**
 * Sample `count` words from a category pool deterministically by seed.
 * Same seed always returns the same sample — which is what the daily-puzzle
 * social-glue story requires.
 *
 * Implementation: Fisher–Yates over a copy of the pool, then take the first
 * `count`. The bank itself was generated by the LLM judge with a bias
 * toward 5–7 letter words, so a uniform sample is already grid-friendly
 * in aggregate — no per-call length-bucketing needed.
 */
export function sampleWordsForRound(
  pool: readonly string[],
  count: number,
  seed: number,
): readonly string[] {
  const rand = makeSeededRandom(seed);
  // Fisher–Yates shuffle over a mutable copy.
  const arr = [...pool];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const a = arr[i]!;
    const b = arr[j]!;
    arr[i] = b;
    arr[j] = a;
  }
  return arr.slice(0, Math.min(count, arr.length));
}

export function atmosphereSrc(slug: CategorySlug, mode: 'light' | 'dark'): string {
  return `/generated/wordsearch-themedshape/${slug}-${mode}.png`;
}
